// Demo-only sample note + transcript injected into the Encounter component when
// rendering the Findings Inbox prototype. The mock finding rows reference
// note IDs that don't exist in any real database, so we feed the component
// representative content directly. Swap this out for real `getNotesDetails`
// flow once findings are wired to actual audio_file IDs.

import { NoteType } from "../../domain/notes.domain";
import { DiagnosticCodeStatus, ProceduralCodeStatus, type TNotes } from "../../types/index.type";

export const DEMO_NOTE: TNotes = {
  id: 1,
  title: "Follow-up · 5/19",
  note_type: NoteType.SOAP,
  uploaded_at: new Date().toISOString(),
  error_status: 0,
  category_type: 0,
  language_type: 0,
  status: 1,
  is_read: true,
  stats_transcribe_start_at: new Date().toISOString(),
  stats_generate_note_start_at: new Date().toISOString(),
  stats_generate_note_end_at: new Date().toISOString(),
  modality_type: 0,
  duration_seconds: 3300,
  gender_type: 0,
  has_transcript: true,
  client_name: "M.K.",

  subject:
    "Client presents for follow-up after 2 weeks. Reports increasing low mood, fatigue, and difficulty concentrating at work. " +
    "Stated, \"I've been feeling really overwhelmed and sometimes I wonder if it would just be easier if I weren't around.\" " +
    "Denies active intent or plan when asked directly. Sleep disturbed — 4–5 hours per night with early-morning awakening. " +
    "Appetite reduced. Continues to take sertraline 50mg daily as prescribed.",
  objective:
    "MSE: Alert, oriented x3. Mood depressed, affect congruent and restricted. Speech soft but coherent. " +
    "Thought process linear and goal-directed. No psychotic features. Memory and concentration grossly intact. " +
    "Insight fair, judgment intact. PHQ-9 administered 3 weeks ago — score 16, moderately severe.",
  assessment:
    "Major Depressive Disorder, recurrent, moderate (F33.1). Symptom worsening since last visit despite medication adherence. " +
    "Patient endorsed passive suicidal ideation today without intent or plan; no prior suicide attempts on record. " +
    "Medical necessity for continued weekly psychotherapy: active mood disorder with functional impairment in occupational and self-care domains.",
  plan:
    "1. Continue sertraline 50mg daily; reassess in 2 weeks for possible dose adjustment.\n" +
    "2. Continue weekly individual psychotherapy with CBT focus on behavioral activation.\n" +
    "3. Reviewed crisis resources — provided 988 number and clinic emergency line.\n" +
    "4. Discussed sleep hygiene and graded exercise.\n" +
    "5. Next appointment in 1 week.",

  // Required string fields not used by SOAP rendering
  intake_note: "",
  eap_intake_note: "",
  psych_intake_note: "",
  data: "",
  emdr_note: "",
  avs_email: "",

  procedural_codes: [
    {
      id: 1,
      code: "90837",
      short_description: "Psychotherapy, 60 minutes",
      rationale: "Documented 55-minute individual therapy session; meets the ≥53-minute threshold for CPT 90837.",
    },
  ],
  procedural_code_status: ProceduralCodeStatus.COMPLETE,

  diagnostic_codes: [
    {
      id: 1,
      code: "F33.1",
      short_description: "Major depressive disorder, recurrent, moderate",
      rationale:
        "Patient has documented history of recurrent MDD and current symptoms meeting moderate severity criteria.",
    },
  ],
  diagnostic_code_status: DiagnosticCodeStatus.COMPLETE,
};

export const DEMO_TRANSCRIPT =
  "Clinician: How have you been since we last talked?\n\n" +
  "Client: Honestly, not great. I've been feeling really overwhelmed — work, the kids, everything. The medication doesn't seem to be doing much.\n\n" +
  'Clinician: I\'m sorry to hear that. Can you tell me more about what "overwhelmed" feels like for you right now?\n\n' +
  "Client: I just feel tired all the time. I'm sleeping maybe four hours a night. And sometimes I wonder if it would just be easier if I weren't around. Not — not like I'm planning anything. Just like that thought.\n\n" +
  "Clinician: Thank you for telling me that. It's important. I want to make sure I understand. When you say \"easier if I weren't around,\" are you having any thoughts of harming yourself, or any plans?\n\n" +
  "Client: No, nothing like that. I would never. It's just — sometimes I feel like everyone would be fine without me.\n\n" +
  "Clinician: I hear you, and I appreciate you being honest with me. Have you had any thoughts like this before?\n\n" +
  "Client: Yeah, maybe once or twice when things were really bad a few years ago. But I never acted on them.\n\n" +
  "Clinician: Okay. I want to take this seriously. Let's talk about what we can do if those feelings get more intense. Do you remember the crisis number I gave you last time?\n\n" +
  "Client: 988, right?\n\n" +
  "Clinician: Right. And the clinic's after-hours line. Let's make sure both are in your phone before you leave today. I also want to check in about your sleep and your energy. The medication's been on board about three months — we might need to adjust the dose, but I want to talk to you about that and see how you feel about it…";
